import {
    LuArchive,
    LuArchiveRestore,
    LuArrowLeftRight,
    LuBell,
    LuBellOff,
    LuClock,
    LuClockAlert,
    LuCopy,
    LuKanban,
    LuListPlus,
    LuFilterX,
    LuMail,
    LuMailOpen,
    LuPanelRight,
    LuPin,
    LuPinOff,
    LuSparkles,
    LuUndo2,
} from 'react-icons/lu';
import type { Contact } from '../data/mockData';
import type { ItemMenuContexto } from './MenuContexto';
import { horarioDoPrazo, type PrazoLembrete, type PrazoSilencio } from '../lib/prazosChat';

/** O que o menu pode fazer com uma conversa. Quem implementa é o ChatPage; a lista e o kebab só chamam. */
export interface AcoesConversa {
    alternarNaoLida: (contato: Contact) => void;
    alternarArquivada: (contato: Contact) => void;
    alternarFixada: (contato: Contact) => void;
    /** Prazo nulo religa os avisos. */
    silenciar: (contato: Contact, prazo: PrazoSilencio | null) => void;
    /** Prazo nulo cancela o lembrete (a conversa volta para a caixa). */
    lembrar: (contato: Contact, prazo: PrazoLembrete | null) => void;
    transferir: (contato: Contact) => void;
    abrirContato: (contato: Contact) => void;
    resumir: (contato: Contact) => void;
    copiarTelefone: (contato: Contact) => void;
    alternarFunil: (contato: Contact) => void;
    criarTarefa: (contato: Contact) => void;
    verNaPipeline: (contato: Contact) => void;
    /**
     * O que a pessoa pode ver neste menu. O item some em vez de aparecer morto: quem não tem
     * `fluxo_vendas` não alcança a Pipeline, e sem tipo de tarefa cadastrado não há como criar tarefa.
     */
    permite: {
        verNaPipeline: boolean;
        criarTarefa: boolean;
    };
}

interface OpcoesMenu {
    /**
     * Só o kebab do cabeçalho passa isto: o painel de Contato já pode estar aberto naquela conversa,
     * e ali o item vira "Fechar o Contato" (era o que o kebab já fazia). Na lista, é sempre "Abrir".
     */
    painel?: { aberto: boolean; alternar: () => void };
}

const ICONE = 14;

/**
 * A lista única de ações da conversa — a do botão direito na lista e a do kebab do cabeçalho.
 * Uma ação, um nome, dois lugares (chat-menu-e-atalhos.html §5).
 *
 * Três seções com título (§17.1): Caixa, Atendimento e Negócio.
 * "Arquivar" e "Tirar do funil" ficam em seções separadas: são o mesmo gesto com consequências
 * opostas — uma é da caixa, a outra é do negócio.
 *
 * Dentro da pasta Arquivadas a conversa só pode ser desarquivada, então "Fixar no topo", "Lembrar depois"
 * e "Silenciar avisos" somem. Dentro de Lembretes, "Lembrar depois" vira "Cancelar lembrete" e "Fixar"
 * também some (a pasta não tem fixadas).
 *
 * "Lembrar depois" e "Silenciar avisos" são os dois itens com submenu de prazo (§17.1): o rótulo de
 * cada prazo mostra o horário que ele resulta, para ninguém precisar calcular.
 */
export const itensDaConversa = (
    contato: Contact,
    acoes: AcoesConversa,
    opcoes: OpcoesMenu = {},
): ItemMenuContexto[] => {
    const itens: ItemMenuContexto[] = [
        {
            id: 'nao-lida',
            rotulo: contato.marcadaNaoLida ? 'Marcar como lida' : 'Marcar como não lida',
            icone: contato.marcadaNaoLida ? <LuMailOpen size={ICONE} /> : <LuMail size={ICONE} />,
            onSelecionar: () => acoes.alternarNaoLida(contato),
        },
        {
            id: 'arquivar',
            rotulo: contato.arquivada ? 'Desarquivar conversa' : 'Arquivar conversa',
            icone: contato.arquivada ? <LuArchiveRestore size={ICONE} /> : <LuArchive size={ICONE} />,
            onSelecionar: () => acoes.alternarArquivada(contato),
        },
    ];

    const agora = Date.now();
    const emLembrete = !!contato.lembrarEm && contato.lembrarEm > agora;
    const silenciada = !!contato.silenciadaAte && contato.silenciadaAte > agora;

    if (!contato.arquivada && !emLembrete) {
        itens.push({
            id: 'fixar',
            rotulo: contato.fixada ? 'Soltar do topo' : 'Fixar no topo',
            icone: contato.fixada ? <LuPinOff size={ICONE} /> : <LuPin size={ICONE} />,
            onSelecionar: () => acoes.alternarFixada(contato),
        });
    }

    if (!contato.arquivada) {
        if (emLembrete) {
            itens.push({
                id: 'lembrar',
                rotulo: 'Cancelar lembrete',
                icone: <LuClockAlert size={ICONE} />,
                onSelecionar: () => acoes.lembrar(contato, null),
            });
        } else {
            const prazoLembrete = (prazo: PrazoLembrete, rotulo: string): ItemMenuContexto => ({
                id: `lembrar-${prazo}`,
                rotulo,
                detalhe: horarioDoPrazo(prazo, agora),
                onSelecionar: () => acoes.lembrar(contato, prazo),
            });
            itens.push({
                id: 'lembrar',
                rotulo: 'Lembrar depois',
                icone: <LuClock size={ICONE} />,
                onSelecionar: () => undefined,
                itens: [
                    prazoLembrete('1h', 'Em 1 hora'),
                    prazoLembrete('amanha', 'Amanhã cedo'),
                    prazoLembrete('segunda', 'Segunda-feira'),
                ],
            });
        }

        if (emLembrete) {
            // Sem efeito: a API já tira Lembretes do sino, então o item não aparece (como "Fixar").
        } else if (silenciada) {
            itens.push({
                id: 'silenciar',
                rotulo: 'Religar avisos',
                icone: <LuBell size={ICONE} />,
                onSelecionar: () => acoes.silenciar(contato, null),
            });
        } else {
            const prazoSilencio = (prazo: PrazoSilencio, rotulo: string): ItemMenuContexto => ({
                id: `silenciar-${prazo}`,
                rotulo,
                onSelecionar: () => acoes.silenciar(contato, prazo),
            });
            itens.push({
                id: 'silenciar',
                rotulo: 'Silenciar avisos',
                icone: <LuBellOff size={ICONE} />,
                onSelecionar: () => undefined,
                itens: [
                    prazoSilencio('8h', 'Por 8 horas'),
                    prazoSilencio('1d', 'Por 1 dia'),
                    prazoSilencio('sempre', 'Até eu religar'),
                ],
            });
        }
    }

    const painel = opcoes.painel;
    const atendimento: ItemMenuContexto[] = [
        {
            id: 'transferir',
            rotulo: 'Transferir para…',
            icone: <LuArrowLeftRight size={ICONE} />,
            onSelecionar: () => acoes.transferir(contato),
        },
        {
            id: 'resumir',
            rotulo: 'Resumir com IA',
            icone: <LuSparkles size={ICONE} />,
            onSelecionar: () => acoes.resumir(contato),
        },
    ];

    if (contato.phone?.replace(/\D/g, '')) {
        atendimento.push({
            id: 'copiar-telefone',
            rotulo: 'Copiar telefone',
            icone: <LuCopy size={ICONE} />,
            onSelecionar: () => acoes.copiarTelefone(contato),
        });
    }

    /* Seção do negócio: o Contato, criar tarefa, ver na Pipeline e o funil (§17.1). */
    const negocio: ItemMenuContexto[] = [
        {
            id: 'contato',
            rotulo: painel?.aberto ? 'Fechar o Contato' : 'Abrir o Contato',
            icone: <LuPanelRight size={ICONE} />,
            onSelecionar: () => (painel ? painel.alternar() : acoes.abrirContato(contato)),
        },
    ];

    if (acoes.permite.criarTarefa) {
        negocio.push({
            id: 'criar-tarefa',
            rotulo: 'Criar tarefa',
            icone: <LuListPlus size={ICONE} />,
            onSelecionar: () => acoes.criarTarefa(contato),
        });
    }

    /* Contato fora do funil não tem cartão na Pipeline: o item some em vez de levar a lugar nenhum. */
    if (acoes.permite.verNaPipeline && !contato.foraDoFunil) {
        negocio.push({
            id: 'ver-pipeline',
            rotulo: 'Ver na Pipeline',
            icone: <LuKanban size={ICONE} />,
            onSelecionar: () => acoes.verNaPipeline(contato),
        });
    }

    /* Sem contato não há como tirar do funil: o item some em vez de aparecer morto. */
    if (contato.contactId) {
        negocio.push({
            id: 'funil',
            rotulo: contato.foraDoFunil ? 'Levar para o funil' : 'Tirar do funil',
            icone: contato.foraDoFunil ? <LuUndo2 size={ICONE} /> : <LuFilterX size={ICONE} />,
            onSelecionar: () => acoes.alternarFunil(contato),
        });
    }

    /* O título vai no primeiro item visível de cada seção: seção sem item não deixa cabeçalho solto. */
    const secoes: [string, ItemMenuContexto[]][] = [
        ['Caixa', itens],
        ['Atendimento', atendimento],
        ['Negócio', negocio],
    ];
    const saida: ItemMenuContexto[] = [];
    for (const [titulo, lista] of secoes) {
        if (!lista.length) continue;
        lista[0].tituloAntes = titulo;
        saida.push(...lista);
    }

    return saida;
};
